import SignupSampleDashboard from '@/components/signup/SignupSampleDashboard';
import { canExploreSignupDashboard, isSignupSendingBlocked } from '@/components/signup/activation';
import axios from 'axios';
import moment from 'moment';
import React, { Component, useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import store from 'store';
import { switchUser } from '@/actions';
import { handleDisableNotificationsOnLogout } from '@/actions/browserNotifications';
import { setActiveTeamConversation } from '@/actions/teams';
import { setShowConversationDetails, setTranscriptView } from '@/actions/userInterface';
import {
  addConversationToCustomerConversations,
  deleteFolderFromAllCustomerConversations,
  failOptimisticMessage,
  getCustomerConversations,
  locallyAddFolderToCustomerConversation,
  removeFolderFromCustomerConversation,
  removeOptimisticMessage,
  setActiveConversation,
  setActiveDashboard,
  setConversationToRead,
  setCustomerConversationAssignedType,
  setCustomerConversations,
  setOpenOrClosedConversationsSelected,
  setShowOnlyUnreadConversations,
  syncCustomerConversationsAfterIdle,
  toggleMarkAsReadConversation,
} from '@/actions/webchat';
import { HideMobileTabBar, MobilePushedPage, useMobilePanel } from '@/components/mobile';
import { getShowNewLayout } from '@/components/sidebar/layout-preference';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { TooltipProvider } from '@/components/ui/tooltip';
import { isMobileViewport, useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { clearEscalationIfClosed } from '@/slices/aiEmployeeSlice';
import { notifyConversationAccessDenied } from '@/utils/conversationAccessDenied';
import { getConversationListName } from '@/types';
import {
  buildContextualCustomerConversationPath,
  buildLegacyCustomerConversationRedirectPath,
  getConversationListPath,
  isTeamConversationPath,
} from '@/utils/customerConversationRoutes';
import getWebchatUrl from '@/utils/getWebchatUrl';
import { getFilteredCustomerConversationsFromSelectionState } from '@/utils/webchat/getFilteredCustomerConversationsFromSelectionState';
import { stripeAccountConnected } from '../../actions/payments';
import { displayNotification } from '../../actions/settings';
import {
  getInitialTeamConversations,
  getSurroundingMessages,
  setActiveTeamConversationToNull,
} from '../../actions/teams';
import getSearchUrl from '../../utils/getSearchUrl';
import { addRecentConversation } from '../../utils/recentConversations';
import usAreaCodes from '../../utils/usAreaCodes';
import { Button } from '../ui/button';
import { MessagesSquare } from 'lucide-react';
import { Dialog } from '../ui/dialog';
import TranscriptionSection from '../webchat/chat/Chatfeed/TranscriptionSection';
import ConversationDetails from '../webchat/chat/ConversationDetails/index';
import { SCHEDULED_EVENT_DATE_FORMAT } from '../webchat/chat/scheduled-messages/useScheduledMessages';
import ConversationTypeSelector from '../webchat/chat/ConversationTypeSelector';
import CustomerList from '../webchat/chat/CustomerList';
import { MobileThreadPlaceholder } from '../webchat/chat/inbox-mobile/MobileThreadPlaceholder';
import ErrorDialog from '../webchat/chat/Dialogs/ErrorDialog';
import PopupDialog from '../webchat/chat/Dialogs/PopupDialog';
import Messages from '../webchat/chat/Messages';
import ComposeView from '../webchat/chat/compose/ComposeView';
import Composer from '../webchat/teams/Composer';
import MessageNavbar from '../webchat/teams/MessageNavbar';
import NewChat from '../webchat/teams/NewChat';
import Sidebar from '../webchat/teams/Sidebar';
import TeamConversations from '../webchat/teams/TeamConversations';
import TeamMessages from '../webchat/teams/TeamMessages';

class WebchatDashboard extends Component {
  state = {
    isSidebarOpen: false,
    customerDialogIsOpen: false,
    isCollapsed:
      localStorage.getItem('react-resizable-panels:collapsed') !== null
        ? JSON.parse(localStorage.getItem('react-resizable-panels:collapsed'))
        : undefined,
    defaultLayout:
      localStorage.getItem('react-resizable-panels:layout') !== null
        ? JSON.parse(localStorage.getItem('react-resizable-panels:layout'))
        : [20, 30, 50],
    oldLayout:
      localStorage.getItem('oldLayout') !== null
        ? JSON.parse(localStorage.getItem('oldLayout'))
        : [20, 30, 50],
    newLayout:
      localStorage.getItem('newLayout') !== null
        ? JSON.parse(localStorage.getItem('newLayout'))
        : [24, 76],
    isAttachmentsModalOpen: false,
    showStartNewChatDialog: false,
    loading: true,
    loadingNewMessage: false,
    unreadConversations: 0,
    incomingMessageOccurred: false,
    incomingConversationTitle: '',
    otherLocationIncomingMessage: false,
    isA2PRegisteredOrInProgress: false,
    byPassA2PPopup: false,
    a2pFailed: false,
    supportEmail: '',
    isBandwidthNumber: true,
    isContactInfoFilled: false,
    openAddNewLocationDialog: false,
    active: 'webchat', // webchat or group-chat
    showNewLayout: false,
    // While true, the user cannot open another conversation from the list (see onSelectConversation).
    // Set from ChatBox during payment-request sends so async invoice + message work cannot run against
    // a different activeConversation than the one they started from (wrong Stripe customer / wrong SMS).
    paymentSendInProgress: false,
    scheduledMessagesRefreshKey: 0,
    // 768-1279px: the details panel covers part of the thread there, so it
    // opens on request instead of following the desktop preference.
    tabletDetailsOpen: false,
    // Width of the list + thread area, for the list's minimum size at 768-1279px.
    groupWidth: 0,
  };

  setPaymentSendInProgress = (value) => {
    this.setState({ paymentSendInProgress: !!value });
  };

  setLoadingNewMessage = (value) => {
    this.setState({ loadingNewMessage: !!value });
  };

  refreshScheduledMessages = () => {
    this.setState((state) => ({
      scheduledMessagesRefreshKey: state.scheduledMessagesRefreshKey + 1,
    }));
  };

  // ChatFeed registers the handler that rewrites the thread event line. The
  // side panel calls it on delete/send-now so both controls update the thread.
  registerScheduledMessageResolved = (handler) => {
    this.onScheduledMessageResolved = handler;
  };

  handleScheduledMessageResolved = (scheduledMessage, action) => {
    this.onScheduledMessageResolved?.(scheduledMessage, action);
  };

  constructor(props) {
    super(props);

    this.chatFeedRef = React.createRef();
    this.panelGroupRef = React.createRef();
    this.resizableGroupRef = React.createRef();
    // The team thread's details button, where focus returns when the
    // 768-1279px info sidebar closes.
    this.teamInfoToggleRef = React.createRef();
    // That sidebar's own close button (768-1279px only).
    this.teamInfoCloseRef = React.createRef();
    this.isDraggingHandle = false;
    this.webchatApiUrl = getWebchatUrl(window.location.href);
  }

  async componentDidMount() {
    if (this.props.isTablet) {
      this.setState({ isCollapsed: true });
    }

    if (this.props.a2pCompliance) {
      this.syncA2PComplianceState(this.props.a2pCompliance);
    }

    const usingNewLayout = getShowNewLayout(this.props.auth);

    this.setState(
      {
        showNewLayout: usingNewLayout,
      },
      () => {
        this.syncCollapsedFromSizes(this.getActiveLayout());
      },
    );

    if (!usingNewLayout && !this.props.isTablet) {
      const collapsed = Array.isArray(this.state.oldLayout) && this.state.oldLayout[0] <= 12;

      if (collapsed !== this.state.isCollapsed) {
        this.setState({ isCollapsed: collapsed });
        localStorage.setItem(`react-resizable-panels:collapsed`, JSON.stringify(collapsed));
      }
    }

    if (!isSignupSendingBlocked(this.props.auth)) this.props.stripeAccountConnected();

    if (window.location.pathname.includes('groups') || window.location.pathname.includes('dms')) {
      this.props.setActiveDashboard('group-chat');
    }
    this.setDocumentTitle();

    window.addEventListener('resize', this.handleResize);

    if (typeof ResizeObserver !== 'undefined' && this.panelGroupRef.current) {
      this.groupResizeObserver = new ResizeObserver(this.measureGroupWidth);
      this.groupResizeObserver.observe(this.panelGroupRef.current);
    }
    this.measureGroupWidth();
  }

  // The inbox and team chat share this component, which stays mounted when
  // switching between them.
  setDocumentTitle = () => {
    const pathname = this.props.location?.pathname || '';
    document.title = pathname.includes('groups') || pathname.includes('dms') ? 'Team Chat' : 'Reviews & Messaging';
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.props.location?.pathname !== prevProps.location?.pathname) {
      this.setDocumentTitle();
    }
    if (this.props.isTablet && !prevProps.isTablet) {
      this.setState({ isCollapsed: true });
    }

    // Back at desktop width: put back the saved panel sizes, which the
    // narrower layouts may have squeezed without saving.
    if (!this.props.isTablet && prevProps.isTablet) {
      this.resizableGroupRef.current?.setLayout?.(this.getActiveLayout());
    }

    // 768-1279px: after rotating from a phone, or when the space changes, put
    // the list where a fresh load would (its minimum or the saved size).
    if (
      this.props.isTablet &&
      !this.props.isMobile &&
      (prevProps.isMobile || prevState.groupWidth !== this.state.groupWidth)
    ) {
      this.settleTabletLayout();
    }

    // 768-1279px: Escape closes the team info sidebar that covers the thread.
    const teamInfoOverlayOpen = this.isTeamInfoOverlayOpen();
    if (teamInfoOverlayOpen !== this.teamInfoEscapeBound) {
      this.teamInfoEscapeBound = teamInfoOverlayOpen;
      if (teamInfoOverlayOpen) document.addEventListener('keydown', this.handleTeamInfoEscape);
      else document.removeEventListener('keydown', this.handleTeamInfoEscape);
    }

    // Rotating to a phone with the details panel open keeps it open as a page.
    if (
      this.props.isMobile &&
      !prevProps.isMobile &&
      this.state.tabletDetailsOpen &&
      this.props.webchat.activeConversation?._id
    ) {
      this.setState({ tabletDetailsOpen: false });
      this.props.openMobileDetails?.();
    }

    // A team chat's info does the same across 768px: the phone's Info screen
    // becomes the sidebar (and its history entry is popped, so Back doesn't
    // land on it), and an open sidebar becomes the Info screen.
    if (this.props.webchat.activeDashboard === 'group-chat') {
      if (!this.props.isMobile && prevProps.isMobile && this.props.teamInfoOpen) {
        // The Info screen had focus and is gone; carry it to the sidebar.
        this.setState({ isSidebarOpen: true }, this.focusTeamInfoSidebar);
        this.props.closeTeamInfo?.();
      } else if (
        this.props.isMobile &&
        !prevProps.isMobile &&
        this.state.isSidebarOpen &&
        this.props.routeTeamConversationId &&
        this.activeConversationExists()
      ) {
        this.setState({ isSidebarOpen: false });
        this.props.openTeamInfo?.();
      }
    }

    // 768-1279px: a call transcript opens the details panel, and stays there
    // after "Back to details".
    const transcriptView = this.props.userInterface.webchat.transcriptView;
    if (
      transcriptView &&
      !prevProps.userInterface.webchat.transcriptView &&
      this.props.isTablet &&
      !this.props.isMobile &&
      !this.state.tabletDetailsOpen
    ) {
      this.setState({ tabletDetailsOpen: true });
    }

    if (this.props.a2pCompliance && this.props.a2pCompliance !== prevProps.a2pCompliance) {
      this.syncA2PComplianceState(this.props.a2pCompliance);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleTeamInfoEscape);
    this.groupResizeObserver?.disconnect();
  }

  isTeamInfoOverlayOpen = () =>
    Boolean(
      this.state.isSidebarOpen &&
        this.props.isTablet &&
        !this.props.isMobile &&
        this.props.webchat.activeDashboard === 'group-chat',
    );

  handleTeamInfoEscape = (event) => {
    // Leave Escape to a dialog or menu opened from the sidebar.
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]')) return;
    // Closing unmounts the notes and drops an unsaved one, so not while typing.
    const field = event.target?.closest?.(
      'textarea, input:not([type]), input[type="text"], input[type="search"], [contenteditable="true"]',
    );
    if ((field?.value ?? field?.textContent)?.trim()) return;
    if (this.isTeamInfoOverlayOpen()) this.closeTeamInfoOverlay();
  };

  // Puts focus on the info sidebar's close button (or, where the sidebar has
  // none, the details button) unless something else already holds it.
  focusTeamInfoSidebar = () => {
    const active = document.activeElement;
    if (active && active !== document.body) return;
    (this.teamInfoCloseRef.current || this.teamInfoToggleRef.current)?.focus();
  };

  // 768-1279px: closing the info sidebar from the keyboard or its close
  // button puts focus back on the details button that opened it.
  closeTeamInfoOverlay = () => {
    this.setState({ isSidebarOpen: false }, () => this.teamInfoToggleRef.current?.focus());
  };

  measureGroupWidth = () => {
    const width = this.panelGroupRef.current?.offsetWidth || 0;
    if (Math.abs(width - this.state.groupWidth) >= 8) {
      this.setState({ groupWidth: width });
    }
  };

  syncA2PComplianceState = (a2pCompliance) => {
    if (!a2pCompliance) {
      return;
    }

    const {
      byPassA2PPopup,
      isA2PRegisteredOrInProgress,
      a2pFailed,
      supportEmail,
      isBandwidthNumber,
      isContactInfoFilled,
    } = a2pCompliance;

    this.setState((prevState) => ({
      byPassA2PPopup:
        typeof byPassA2PPopup === 'boolean' ? byPassA2PPopup : prevState.byPassA2PPopup,
      isA2PRegisteredOrInProgress:
        typeof isA2PRegisteredOrInProgress === 'boolean'
          ? isA2PRegisteredOrInProgress
          : prevState.isA2PRegisteredOrInProgress,
      a2pFailed: typeof a2pFailed === 'boolean' ? a2pFailed : prevState.a2pFailed,
      supportEmail: supportEmail || prevState.supportEmail,
      isBandwidthNumber:
        typeof isBandwidthNumber === 'boolean' ? isBandwidthNumber : prevState.isBandwidthNumber,
      isContactInfoFilled:
        typeof isContactInfoFilled === 'boolean'
          ? isContactInfoFilled
          : prevState.isContactInfoFilled,
    }));
  };

  handleLogout = async () => {
    await this.props.handleDisableNotificationsOnLogout();
    store.remove('jwt');
    window.location = `https://${window.location.hostname}/api/logout`;
  };

  toggleSidebar = () => {
    this.setState({ isSidebarOpen: !this.state.isSidebarOpen });
  };

  // Route-driven compose mode (/inbox/new) — renders ComposeView in the
  // Messages panel slot.
  isComposeMode = () => Boolean(this.props.location?.pathname?.endsWith('/new'));

  handleCustomerDialogIsOpen = (customerDialogIsOpenState) => {
    // "New conversation" now opens the compose view instead of the old
    // customer-lookup modal (GROUP_SMS_PLAN.md Phase 2). All triggers (New
    // button, cmd+K, InboxOverview, search-bar create) funnel through here.
    if (customerDialogIsOpenState) {
      this.props.navigate('/inbox/new');
      return;
    }
    this.setState({ customerDialogIsOpen: customerDialogIsOpenState });
  };

  handleIsAttachmentsModalOpen = (isAttachmentsModalOpenState) => {
    this.setState({
      isAttachmentsModalOpen: isAttachmentsModalOpenState,
    });
  };

  handleShowStartNewChatDialog = (showStartNewChatDialogState) => {
    this.setState({ showStartNewChatDialog: showStartNewChatDialogState });
  };

  activeConversationExists = () => {
    if (this.props.webchat.activeDashboard === 'group-chat') {
      const { teams } = this.props;
      const activeTeamConversation = teams?.teamConversations?.find(
        (conv) => conv._id === teams?._activeTeamConversation,
      );
      return activeTeamConversation;
    }

    return this.props.webchat.activeConversation?._id;
  };

  // If the business hasn't filled in their contact info
  // Remind them they need to complete the contact info once per day
  remindFillInPointOfContactInfo = async () => {
    if (isSignupSendingBlocked(this.props.auth)) return;
    const { isContactInfoFilled, isBandwidthNumber } = this.state;

    // only show popup for US customers that are non-bandwidth
    if (this.props.auth?.twilioNumber && !isBandwidthNumber) {
      const firstTimeContactInfoPopupToday = localStorage.getItem('firstTimeContactInfoPopupToday');

      if (
        // check if contact info is not filled
        !isContactInfoFilled &&
        (!firstTimeContactInfoPopupToday ||
          !this.isSameDay(new Date(firstTimeContactInfoPopupToday), new Date()))
      ) {
        // Store the current date in the local storage
        localStorage.setItem('firstTimeContactInfoPopupToday', new Date().toISOString());

        this.setState({
          loading: false,
          popupMessage: `Keep your account and contact information up to date! Be sure to list your business information and primary contact in our new section in your settings page.
				\nGo to "Settings -> Business Info" on your dashboard.
				\nProvide accurate contact info and click save.`,
          popupTitle: 'Contact Info',
          popupLink: `https://${window.location.hostname}/business-settings`,
          openPopupDialog: true,
        });
      }
    }
  };

  // sending a message to a customer from the main page, NOT when selecting one from the customer list
  sendMessageToNewCustomer = async (messageData) => {
    try {
      const {
        phone,
        name,
        language,
        shouldCloseConversation,
        scheduledSendDate,
        attachmentUrls,
        _location,
      } = messageData;

      if (!name) {
        this.setState({
          loading: false,
          errorMessage: `Please enter your client's name.`,
          errorTitle: 'Missing Name',
          openErrorDialog: true,
        });

        return { error: true };
      }

      if (!phone) {
        this.setState({
          loading: false,
          errorMessage: `Please enter your client's phone or email.`,
          errorTitle: 'Missing phone',
          openErrorDialog: true,
        });

        return { error: true };
      }

      // handle phone case
      if (phone && !phone.includes('@') && !phone.length === 12) {
        this.setState({
          loading: false,
          errorMessage: `Please enter a valid phone number.`,
          errorTitle: 'Invalid phone',
          openErrorDialog: true,
        });

        return { error: true };
      }

      // handle email case
      if (phone?.includes('@') && phone.length < 6) {
        this.setState({
          loading: false,
          errorMessage: `Please enter a valid email.`,
          errorTitle: 'Invalid email',
          openErrorDialog: true,
        });

        return { error: true };
      }

      this.remindA2PRegistration();
      // this.remindFillInPointOfContactInfo(); // disable while we ask for a2p registration

      const phoneEmail = messageData.phone;

      this.setState({ loading: true });

      const { customerConversations } = this.props.webchat;

      const employee = localStorage.getItem('employeeName') || '';

      const dateFormat = SCHEDULED_EVENT_DATE_FORMAT;

      const url = scheduledSendDate
        ? '/api/schedule-single-message'
        : '/api/webchat/business-message-to-new-customer';

      // create a UTC date for the scheduled message
      const utcScheduledSendDate = moment(scheduledSendDate).utc().toDate();

      // send message
      const res = await axios(`${this.webchatApiUrl}${url}`, {
        method: 'post',
        data: {
          phoneEmail,
          attachmentUrls,
          name: messageData.name,
          text: messageData.text,
          media: messageData.media,
          fromWebsocketId: this.props.webchat.socketId,
          employee,
          language,
          shouldCloseConversation,
          scheduledSendDate: utcScheduledSendDate,
          localScheduledSendDate: scheduledSendDate ? scheduledSendDate.format(dateFormat) : null,
          _business: _location,
        },
        withCredentials: true,
      });

      if (res.data.error) {
        const errorMessage = res.data.message || `Error sending message, please try again.`;

        this.setState({
          loading: false,
          errorMessage,
          errorTitle: 'Error',
          openErrorDialog: true,
        });

        return { error: true };
      }

      const updatedActiveConversation = res.data.customerConversation;

      const allOtherConversations = customerConversations.filter(
        (conversation) => conversation._id !== updatedActiveConversation._id,
      );

      this.props.setCustomerConversations([updatedActiveConversation, ...allOtherConversations]);

      this.props.setActiveConversation(updatedActiveConversation._id);

      // This screen's own socket is left out of the broadcast, so the answer is
      // how it learns that the reply closed the AI's hand-off.
      this.props.clearEscalationIfClosed(updatedActiveConversation);

      this.setState({
        loading: false,
      });

      // customerConversation included so the compose view can navigate to the
      // newly created conversation; existing callers only read `error`.
      return { error: false, customerConversation: updatedActiveConversation };
    } catch (err) {
      console.log('error sending message ', err);
      this.setState({
        loading: false,
        errorMessage: `Error sending message, please try again!`,
        errorTitle: 'Error',
        openErrorDialog: true,
      });

      return { error: true };
    }
  };

  isUSPhoneNumber = (phoneNumber) => {
    // Check if the phone number starts with "+1"
    if (!phoneNumber.startsWith('+1')) {
      return false;
    }

    // Extract the area code from the phone number
    const areaCode = phoneNumber.substring(2, 5);

    // Check if the area code is in the list of US area codes
    return usAreaCodes.includes(areaCode);
  };

  setShowTeams = (isShowTeams) => {
    this.props.setActiveDashboard(isShowTeams ? 'group-chat' : 'webchat');
  };

  // Function to check if two dates are the same day
  isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  // If the business hasn't registered yet,
  // Remind them they need to complete the a2p registration once per day
  remindA2PRegistration = async () => {
    if (isSignupSendingBlocked(this.props.auth)) return;
    const {
      byPassA2PPopup,
      isA2PRegisteredOrInProgress,
      a2pFailed,
      supportEmail,
      isBandwidthNumber,
    } = this.state;

    if (byPassA2PPopup) {
      return;
    }

    // only show popup for US customers that are non-bandwidth
    if (
      this.props.auth?.twilioNumber &&
      this.isUSPhoneNumber(this.props.auth.twilioNumber) &&
      !isBandwidthNumber
    ) {
      const firstTimeMessageSentToday = localStorage.getItem('firstTimeMessageSentToday');

      if (
        // check if a2p registration failed
        // open popup if this is the first time the message is sent today
        a2pFailed &&
        (!firstTimeMessageSentToday ||
          !this.isSameDay(new Date(firstTimeMessageSentToday), new Date()))
      ) {
        // Store the current date in the local storage
        localStorage.setItem('firstTimeMessageSentToday', new Date().toISOString());

        this.setState({
          loading: false,
          popupMessage: `The information you provided for A2P registration has been rejected by our telecom carrier. Please reach out to ${supportEmail} to go through this with our team.`,
          popupTitle: 'A2P Compliance Registration',
          popupLink: `https://${window.location.hostname}/compliance-settings`,
          openPopupDialog: true,
        });
      } else if (
        // check if they have already started/completed the registration before sending the error message
        // and they have not registered for a2p
        !isA2PRegisteredOrInProgress &&
        (!firstTimeMessageSentToday ||
          !this.isSameDay(new Date(firstTimeMessageSentToday), new Date()))
      ) {
        // Store the current date in the local storage
        localStorage.setItem('firstTimeMessageSentToday', new Date().toISOString());

        this.setState({
          loading: false,
          popupMessage: `Your messages may be getting filtered due to incomplete registration.
					\nComplete A2P Registration now!
				\nGo to "Settings -> Compliance" on your dashboard.
				\nEnsure message deliverability. Provide accurate A2P registration info and click "Submit" to save.`,
          popupTitle: 'A2P Compliance Registration',
          popupLink: `https://${window.location.hostname}/compliance-settings`,
          openPopupDialog: true,
        });
      }
    }
  };

  // This is used only whenever a business wants to RESPOND to an EXISTING customer either by text or facebook
  sendMessage = async (message) => {
    // clientId is supplied by ChatBox so the optimistic bubble can be
    // reconciled with the persisted server message. Capture the active
    // conversation id up-front so reconciliation can't race against
    // thread-switching.
    const startingActiveConversationId = this.props.webchat.activeConversation?._id;
    const clientId = message?.clientId;

    try {
      this.remindA2PRegistration();

      // this.remindFillInPointOfContactInfo(); // disable while we ask for a2p registration

      // activeCustomer is the customer who is currently selected
      const { activeConversation, customerConversations } = this.props.webchat;

      // group conversations send through the group endpoint (no _customer)
      if (activeConversation.isGroup) {
        if (message.scheduledSendDate) {
          this.setState({ loading: false });
          return {
            error: true,
            message: 'Scheduling isn’t available for group messages yet.',
          };
        }

        const groupRes = await axios.post(
          `${this.webchatApiUrl}/api/webchat/business-message-to-group`,
          {
            _customers: (activeConversation._customers || []).map((c) => c._id || c),
            text: message.text,
            attachmentUrls: message.attachmentUrls,
            fromWebsocketId: this.props.webchat.socketId,
            clientId,
          },
          { withCredentials: true },
        );

        if (!groupRes.data || groupRes.data.error) {
          if (clientId && startingActiveConversationId) {
            this.props.failOptimisticMessage({
              _conversation: startingActiveConversationId,
              clientId,
              errorMessage: groupRes.data?.message || 'There was an error sending your message.',
            });
          }
          this.setState({ loading: false });
          return {
            error: true,
            message: groupRes.data?.message || 'There was an error sending your message.',
          };
        }

        const updatedGroupConversation = groupRes.data.customerConversation;

        if (clientId && startingActiveConversationId) {
          this.props.removeOptimisticMessage({
            _conversation: startingActiveConversationId,
            clientId,
          });
        }

        this.props.setCustomerConversations([
          updatedGroupConversation,
          ...customerConversations.filter((c) => c._id !== updatedGroupConversation._id),
        ]);
        this.props.setActiveConversation(updatedGroupConversation._id);
        this.setState({ loading: false });

        return { error: false };
      }

      const facebookMessage =
        !!activeConversation._customer.facebookUserId ||
        !!activeConversation._customer.instagramUserId;

      // default to always send to phone, if phone is available
      // TODO: send back to channel that last message was sent from
      const phoneMessage = !!activeConversation._customer.phone;

      const apiLocation =
        facebookMessage && !phoneMessage
          ? 'business-message-facebook'
          : 'business-message-to-customer';

      const {
        text,
        attachmentUrls,
        language,
        shouldCloseConversation,
        tag,
        scheduledSendDate,
        sendInChannel,
        aiDraftId,
      } = message;

      const dateFormat = SCHEDULED_EVENT_DATE_FORMAT;

      const url = scheduledSendDate
        ? '/api/schedule-single-message'
        : `/api/webchat/${apiLocation}`;

      // create a UTC date for the scheduled message
      const utcScheduledSendDate = moment(scheduledSendDate).utc().toDate();

      const postData = {
        _customer: activeConversation._customer._id,
        _business: activeConversation._business,
        text,
        attachmentUrls,
        language,
        shouldCloseConversation,
        tag,
        fromWebsocketId: this.props.webchat.socketId,
        scheduledSendDate: utcScheduledSendDate,
        localScheduledSendDate: scheduledSendDate ? scheduledSendDate.format(dateFormat) : null,
        sendInChannel,
        clientId,
        aiDraftId,
      };

      if (facebookMessage && attachmentUrls.length) {
        // Destructure the first element of the array
        const [firstUrl] = attachmentUrls;

        // Add the firstUrl to object
        postData.media = firstUrl;
      }

      // Post to API w/ message data
      const res = await axios(`${this.webchatApiUrl}${url}`, {
        method: 'post',
        data: postData,
        withCredentials: true,
      });

      // facebook 24 hours error — caller handles tag picker. Mark the
      // optimistic bubble failed so the user can click to restore.
      if (res.data?.errorTag) {
        if (clientId && startingActiveConversationId) {
          this.props.failOptimisticMessage({
            _conversation: startingActiveConversationId,
            clientId,
            errorMessage: res.data.message,
          });
        }
        this.setState({ loading: false });
        return {
          error: true,
          errorTag: res.data.errorTag,
          message: res.data.message,
        };
      }

      if (!res.data || res.data.error) {
        if (clientId && startingActiveConversationId) {
          this.props.failOptimisticMessage({
            _conversation: startingActiveConversationId,
            clientId,
            errorMessage: res.data?.message || 'There was an error sending your message.',
          });
        }
        this.setState({ loading: false });
        return {
          error: true,
          message: res.data?.message || 'There was an error sending your message.',
        };
      }

      const updatedActiveConversation = res.data.customerConversation;

      // Remove the optimistic bubble before merging the server conversation
      // so the server's persisted message (with the same clientId on
      // rawPayload) replaces it cleanly.
      if (clientId && startingActiveConversationId) {
        this.props.removeOptimisticMessage({
          _conversation: startingActiveConversationId,
          clientId,
        });
      }

      const allOtherConversations = customerConversations.filter(
        (conversation) => conversation._id !== updatedActiveConversation._id,
      );

      this.props.setCustomerConversations([updatedActiveConversation, ...allOtherConversations]);

      this.props.setActiveConversation(updatedActiveConversation._id);

      // This screen's own socket is left out of the broadcast, so the answer is
      // how it learns that the reply closed the AI's hand-off.
      this.props.clearEscalationIfClosed(updatedActiveConversation);

      this.setState({ loading: false });

      return { error: false };
    } catch (err) {
      console.log('Error sending customer message', err);

      let errorMessage = `Error sending message, please refresh the page and try again.`;

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      const errorTag = err?.response?.data?.errorTag;

      // Mark the optimistic bubble failed so the user can click-to-retry.
      if (clientId && startingActiveConversationId) {
        this.props.failOptimisticMessage({
          _conversation: startingActiveConversationId,
          clientId,
          errorMessage,
        });
      }

      this.setState({ loading: false });

      if (errorTag) {
        return { error: true, errorTag };
      }

      return { error: true, message: errorMessage };
    }
  };

  // Save an internal in-conversation note. Notes are team-only and never
  // delivered to the customer, so they go to a dedicated endpoint. We reuse the
  // same optimistic-bubble reconciliation flow as sendMessage (clientId).
  sendNote = async (note) => {
    const clientId = note?.clientId;

    // Use the conversation context captured by the composer rather than reading
    // live Redux state. This guarantees the persisted note and its optimistic
    // bubble always target the same conversation, even if the active
    // conversation changes during the async send. Fall back to the active
    // conversation for any caller that doesn't pass explicit context.
    const { activeConversation } = this.props.webchat;
    const _conversation = note?._conversation || activeConversation?._id;
    const _customer = note?._customer || activeConversation?._customer?._id;
    const _business = note?._business || activeConversation?._business;

    try {
      if (!_customer || !_conversation) {
        this.setState({ loading: false });
        return {
          error: true,
          message: 'Notes are only available inside a customer conversation.',
        };
      }

      const res = await axios.post(
        `${this.webchatApiUrl}/api/webchat/business-note-to-conversation`,
        {
          _customer,
          _business,
          text: note.text,
          mentions: note.mentions,
          // Private file keys from the teams service ({ key, name, type, size })
          attachments: note.attachments?.length ? note.attachments : undefined,
          requestId: note.requestId || clientId,
          fromWebsocketId: this.props.webchat.socketId,
          clientId,
        },
        { withCredentials: true },
      );

      if (!res.data || res.data.error) {
        if (clientId && _conversation) {
          this.props.failOptimisticMessage({
            _conversation,
            clientId,
            errorMessage: res.data?.message || 'There was an error saving your note.',
          });
        }
        this.setState({ loading: false });
        return {
          error: true,
          message: res.data?.message || 'There was an error saving your note.',
        };
      }

      const updatedConversation = res.data.customerConversation;

      if (clientId && _conversation) {
        this.props.removeOptimisticMessage({
          _conversation,
          clientId,
        });
      }

      const allOtherConversations = this.props.webchat.customerConversations.filter(
        (conversation) => conversation._id !== updatedConversation._id,
      );

      this.props.setCustomerConversations([updatedConversation, ...allOtherConversations]);

      // Only refresh the active view if the user is still on the note's
      // conversation — never yank them away from one they've navigated to.
      if (this.props.webchat.activeConversation?._id === updatedConversation._id) {
        this.props.setActiveConversation(updatedConversation._id);
      }

      this.setState({ loading: false });

      return { error: false };
    } catch (err) {
      console.error('note_save_failed', { name: err.name, status: err.response?.status });

      let errorMessage = 'Error saving note, please refresh the page and try again.';

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      if (clientId && _conversation) {
        this.props.failOptimisticMessage({
          _conversation,
          clientId,
          errorMessage,
        });
      }

      this.setState({ loading: false });

      return { error: true, message: errorMessage };
    }
  };

  saveCustomer = async ({ name, phoneEmail, _location }) => {
    const customer = { name, phoneEmail, _location };

    this.setState({ loading: true });

    const res = await axios.post('/api/create-customer', {
      customer,
    });

    this.setState({ loading: false });

    const { data } = res;

    if (data.customerConversation) {
      const { customerConversation } = data;
      this.props.addConversationToCustomerConversations(customerConversation, true);
    } else {
      const message =
        data.message ||
        'Sorry, we could not create your customer. Please refresh the page and try again.';
      alert(message);
    }
  };

  getNextCustomers = async (channelFilter = 'All') => {
    // Convert frontend channel filter to backend source value
    const channelSourceMap = {
      SMS: 'sms',
      Email: 'email',
      'Google Business Message': 'googleBusinessMessage',
      Facebook: 'facebook',
      Instagram: 'instagram',
      Textchat: 'webchat',
    };

    const backendChannelFilter =
      channelFilter === 'All' ? undefined : channelSourceMap[channelFilter] || channelFilter;

    this.props.getCustomerConversations({
      channelFilter: backendChannelFilter,
    });
  };

  toggleMarkAsReadConversation = async ({ conversation, isRead }) => {
    this.props.toggleMarkAsReadConversation({ conversation, isRead });
  };

  clearMessageSearch = async () => {
    try {
      this.setState({ loading: true });

      this.props.getCustomerConversations({ initial: true });

      this.setState({ loading: false });
    } catch (error) {
      console.error('Error clearing messages', error);
      this.props.displayNotification({
        message: 'Error clearing messages. Please refresh the page and try again.',
        error: true,
      });
      this.setState({ loading: false });
    }
  };

  // this is passed down to CustomerList grandChild and used there
  searchForMessage = async (searchTerm) => {
    try {
      this.setState({ loading: true });

      const res = await axios.post(`${getSearchUrl(window.location.href)}/api/message-search`, {
        searchTerm,
        isEnterpriseEnabled: this.props.auth.user.isEnterpriseEnabled,
      });

      this.props.setCustomerConversations(res.data.customerConversations);

      this.setState({ loading: false });
    } catch (err) {
      console.log('Error searching for messages', err);
      this.props.displayNotification({
        message: 'Error searching for messages. Please refresh the page and try again.',
        error: true,
      });
      this.setState({ loading: false });
    }
  };

  // newType === "ALL" || "UNASSIGNED" || "MY_MESSAGES" || "CONTACTS" || "USER_" || "DEPARTMENT_" || "LOCATION_" || "GROUPS" || "DIRECT_MESSAGES" || "NEW_CHAT" || "FOLDER_"
  setCustomerConversationAssignedType = (newStatus) => {
    this.props.setCustomerConversationAssignedType(newStatus);

    this.decideToLoadMoreConversations(newStatus);
  };

  setOpenOrClosedConversationsSelected = (openConversationsSelected) => {
    this.props.setOpenOrClosedConversationsSelected(openConversationsSelected);
    this.decideToLoadMoreConversations(openConversationsSelected);
  };

  decideToLoadMoreConversations = (newStatus) => {
    // do not load more if group chat options are selected
    if (newStatus === 'GROUPS' || newStatus === 'DIRECT_MESSAGES' || newStatus === 'NEW_CHAT') {
      return;
    }
    // check if we need to load more conversations for the new selection.
    // if there is less than 10 conversations, load more
    // const selectedConversations =
    // 	getFilteredCustomerConversationsFromSelectionState(
    // 		this.state.webchat
    // 	);

    // if (selectedConversations.length < LOADED_CONVERSATION_THRESHOLD) {
    // 	this.props.getCustomerConversations();
    // }
  };

  // fire when selecting conversation from the customer list
  onSelectConversation = (conversation) => {
    if (this.state.paymentSendInProgress) {
      return;
    }

    // If the selected conversation is already active, do nothing to avoid re-focusing the composer.
    // Phones go by the URL: the list only shows when no conversation is open.
    const currentActiveId = this.props.isMobile
      ? this.props.routeCustomerConversationId
      : this.props.webchat.activeConversation?._id;

    if (currentActiveId === conversation._id) {
      return;
    }

    // set the conversation to read
    this.props.setConversationToRead({ conversation });

    this.setOpenOrClosedConversationsSelected(conversation.open);

    this.props.navigate(
      buildContextualCustomerConversationPath({
        pathname: this.props.location.pathname,
        _customerConversation: conversation._id,
        _business: conversation._business,
      }),
    );

    addRecentConversation(conversation);

    // A phone opens the thread without raising the keyboard over it.
    if (this.props.isMobile) {
      return;
    }

    const el = document.getElementById('chatBoxTextBox');

    if (el) {
      el.focus();
    }
  };

  setShowOnlyUnreadConversations = (showOnlyUnreadConversations) => {
    this.props.setShowOnlyUnreadConversations(showOnlyUnreadConversations);
    this.decideToLoadMoreConversations(showOnlyUnreadConversations);
  };

  handleClosePopupDialog = () => {
    this.setState({
      popupMessage: '',
      popupTitle: '',
      openPopupDialog: false,
    });
  };

  handleCloseErrorDialog = () => {
    this.setState({
      openErrorDialog: false,
    });
  };

  renderErrorDialog = () => (
    <ErrorDialog
      open={this.state.openErrorDialog}
      message={this.state.errorMessage}
      title={this.state.errorTitle}
      handleCloseErrorDialog={this.handleCloseErrorDialog}
    />
  );

  renderPopupDialog = () => (
    <PopupDialog
      open={this.state.openPopupDialog}
      message={this.state.popupMessage}
      title={this.state.popupTitle}
      link={this.state.popupLink}
      handleClosePopupDialog={this.handleClosePopupDialog}
    />
  );

  toggleShowConversationDetails = () => {
    const { isMobile, isTablet } = this.props;

    if (isMobile) {
      if (this.props.mobileDetailsOpen) this.props.closeMobileDetails?.();
      else this.props.openMobileDetails?.();
      return;
    }

    if (isTablet) {
      if (this.isDetailsShown()) {
        this.setState({ tabletDetailsOpen: false });
        if (this.props.mobileDetailsOpen) this.props.closeMobileDetails?.();
        if (this.props.userInterface.webchat.transcriptView) this.props.setTranscriptView(null);
      } else {
        this.setState({ tabletDetailsOpen: true });
      }
      return;
    }

    this.props.setShowConversationDetails(
      !this.props.userInterface.webchat.showConversationDetails,
    );
  };

  // Whether the details panel shows beside the thread (not used on phones,
  // where it is a pushed page).
  isDetailsShown = () => {
    const { isMobile, isTablet, mobileDetailsOpen, userInterface } = this.props;
    if (isMobile) return Boolean(mobileDetailsOpen);
    if (isTablet) {
      return Boolean(
        this.state.tabletDetailsOpen || mobileDetailsOpen || userInterface.webchat.transcriptView,
      );
    }
    return userInterface.webchat.showConversationDetails;
  };

  // Get the active layout (oldLayout or newLayout) based on which layout is being shown
  getActiveLayout = () => {
    return this.state.showNewLayout ? this.state.newLayout : this.state.oldLayout;
  };

  // Save the layout to localStorage and update state
  saveLayout = (sizes) => {
    // Save to the appropriate layout based on which layout is active
    if (this.state.showNewLayout) {
      localStorage.setItem('newLayout', JSON.stringify(sizes));
      this.setState({ newLayout: sizes });
    } else {
      localStorage.setItem('oldLayout', JSON.stringify(sizes));

      const collapsed = Array.isArray(sizes) ? sizes[0] <= 12 : this.state.isCollapsed;

      this.setState({ oldLayout: sizes, isCollapsed: collapsed });
      localStorage.setItem(`react-resizable-panels:collapsed`, JSON.stringify(collapsed));
    }
    // Also save to the legacy key for backward compatibility
    localStorage.setItem('react-resizable-panels:layout', JSON.stringify(sizes));
  };

  handleLayoutChange = (sizes) => {
    // Below desktop width the panels get squeezed to their minimum sizes; only
    // a drag there is the user's choice, so only that is saved.
    if (this.props.isMobile) {
      return;
    }
    if (!this.props.isTablet || this.isDraggingHandle) {
      this.saveLayout(sizes);
    }
    this.syncCollapsedFromSizes(sizes);
  };

  handleDraggingHandle = (isDragging) => {
    this.isDraggingHandle = isDragging;
  };

  // 768-1279px: keep the list wide enough to read (about 280px).
  getListMinSize = () => {
    const baseMinSize = this.state.showNewLayout ? 22 : 30;
    const { isTablet, isMobile } = this.props;
    // The live width: right after rotating from a phone, state still holds
    // the phone's width, which would ask for a much larger share.
    const groupWidth = this.panelGroupRef.current?.offsetWidth || this.state.groupWidth;
    if (!isTablet || isMobile || !groupWidth) {
      return baseMinSize;
    }
    return Math.min(45, Math.max(baseMinSize, (280 / groupWidth) * 100));
  };

  // 768-1279px: the list at its minimum, or at the size the user dragged it
  // to if that is wider. The panels only grow to a new minimum by themselves,
  // so a list grown for a narrower space would otherwise stay too wide.
  settleTabletLayout = () => {
    const group = this.resizableGroupRef.current;
    const layout = group?.getLayout?.();
    if (!Array.isArray(layout) || layout.length < 2) return;

    const listIndex = this.state.showNewLayout ? 0 : 1;
    const threadIndex = layout.length - 1;
    const savedListSize = this.getActiveLayout()?.[listIndex] ?? 0;
    const listSize = Math.max(this.getListMinSize(), savedListSize);
    const delta = listSize - layout[listIndex];
    if (Math.abs(delta) < 0.5 || layout[threadIndex] - delta < 30) return;

    const next = [...layout];
    next[listIndex] = listSize;
    next[threadIndex] = layout[threadIndex] - delta;
    group.setLayout(next);
  };

  handleResize = () => {
    this.syncCollapsedFromSizes(this.getActiveLayout());
  };

  syncCollapsedFromSizes = (sizes) => {
    if (this.state.showNewLayout) {
      return;
    }

    const containerWidth = this.panelGroupRef.current?.offsetWidth;

    if (!containerWidth || !Array.isArray(sizes)) {
      return;
    }

    const sidebarWidthPx = (containerWidth * sizes[0]) / 100;
    const shouldCollapse = sidebarWidthPx < 140;

    if (shouldCollapse !== this.state.isCollapsed) {
      this.setState({ isCollapsed: shouldCollapse });
      localStorage.setItem(`react-resizable-panels:collapsed`, JSON.stringify(shouldCollapse));
    }
  };

  // Phones show the list or the open thread, never both, decided by the URL.
  isShowingMobileThread = () => {
    if (!this.props.isMobile) return false;
    if (this.props.webchat.activeDashboard === 'group-chat') {
      return Boolean(this.props.routeTeamConversationId);
    }
    return Boolean(this.props.routeCustomerConversationId) || this.isComposeMode();
  };

  // Phones: while an opened conversation loads (deep link, notification),
  // show its shape with a working back button.
  renderMobileThreadPlaceholder = () => {
    const { isMobile, webchat, teams, routeCustomerConversationId, routeTeamConversationId } =
      this.props;
    if (!isMobile) return null;

    if (webchat.activeDashboard === 'group-chat') {
      if (!routeTeamConversationId || routeTeamConversationId === teams?._activeTeamConversation) {
        return null;
      }
      return <MobileThreadPlaceholder onBack={this.props.onMobileBack} />;
    }

    if (
      !routeCustomerConversationId ||
      this.isComposeMode() ||
      routeCustomerConversationId === webchat.activeConversation?._id
    ) {
      return null;
    }
    const listed = webchat.customerConversations?.find((c) => c._id === routeCustomerConversationId);
    const isActivated = canExploreSignupDashboard(this.props.auth) ? true : this.props.auth.activated;
    return (
      <MobileThreadPlaceholder
        title={(listed && getConversationListName(listed, isActivated)) || 'Loading…'}
        onBack={this.props.onMobileBack}
      />
    );
  };

  renderConversationDetails = (extraProps = {}) => (
    <ConversationDetails
      messages={this.props.webchat.activeConversation.messages || []}
      activeConversation={this.props.webchat.activeConversation}
      addConversationToCustomerConversations={this.props.addConversationToCustomerConversations}
      user={this.props.user}
      users={this.props.users}
      auth={this.props.auth}
      scheduledMessagesRefreshKey={this.state.scheduledMessagesRefreshKey}
      refreshScheduledMessages={this.refreshScheduledMessages}
      onScheduledMessageResolved={this.handleScheduledMessageResolved}
      key={this.props.webchat.activeConversation?._id}
      toggleShowConversationDetails={this.toggleShowConversationDetails}
      showConversationDetails={this.isDetailsShown()}
      {...extraProps}
    />
  );

  // Phone screens pushed over the thread: the conversation's details, a call
  // transcript, and a team chat's info.
  renderMobilePages = () => {
    const { isMobile, webchat, userInterface } = this.props;
    if (!isMobile) return null;
    const transcriptView = userInterface.webchat.transcriptView;
    const hasConversation = Boolean(webchat.activeConversation?._id);
    const isInbox = webchat.activeDashboard === 'webchat' && !this.isComposeMode();

    return (
      <>
        <MobilePushedPage
          open={Boolean(isInbox && hasConversation && this.props.mobileDetailsOpen)}
          onClose={this.props.closeMobileDetails}
          title="Details"
        >
          {this.renderConversationDetails({
            isMobile: true,
            showConversationDetails: true,
            toggleShowConversationDetails: this.props.closeMobileDetails,
          })}
        </MobilePushedPage>
        <MobilePushedPage
          open={Boolean(isInbox && transcriptView && this.props.transcriptOpen)}
          onClose={this.props.closeTranscript}
          title="Call transcript"
        >
          {transcriptView && (
            <TranscriptionSection
              transcription={transcriptView.transcription}
              transcriptionStatus={transcriptView.transcriptionStatus}
              isVoicemail={transcriptView.isVoicemail}
              agentName={transcriptView.agentName}
            />
          )}
        </MobilePushedPage>
      </>
    );
  };

  render() {
    const activeConversationExists = this.activeConversationExists();
    const currentLayout = this.getActiveLayout();
    const { isMobile } = this.props;
    const showMobileThread = this.isShowingMobileThread();
    const detailsShown = this.isDetailsShown();

    return (
      <div ref={this.panelGroupRef} className="chat-dashboard h-full min-h-0 w-full overflow-hidden flex flex-col font-normal antialiased">
        <HideMobileTabBar active={showMobileThread} />
        <Dialog open={this.state.openErrorDialog}>
          {this.renderErrorDialog()}
          {this.renderPopupDialog()}
        </Dialog>
        <TooltipProvider>
          <ResizablePanelGroup
            ref={this.resizableGroupRef}
            className="h-full w-full rounded-xl overflow-hidden max-md:relative max-md:rounded-none"
            direction="horizontal"
            onLayout={this.handleLayoutChange}
          >
            {!this.state.showNewLayout && (
              <>
                <ResizablePanel
                  defaultSize={currentLayout[0]}
                  maxSize={15}
                  minSize={15}
                  collapsible={true}
                  onCollapse={() => {
                    this.setState({ isCollapsed: true });
                    localStorage.setItem(`react-resizable-panels:collapsed`, JSON.stringify(true));
                  }}
                  onExpand={() => {
                    this.setState({ isCollapsed: false });
                    localStorage.setItem(`react-resizable-panels:collapsed`, JSON.stringify(false));
                  }}
                  className={cn(
                    this.state.isCollapsed &&
                      'min-w-[50px] transition-all duration-300 ease-in-out',
                    'max-md:hidden',
                  )}
                >
                  <ConversationTypeSelector
                    deleteFolderFromAllCustomerConversations={
                      this.props.deleteFolderFromAllCustomerConversations
                    }
                    setCustomerConversationAssignedType={this.setCustomerConversationAssignedType}
                    assignedTypeSelected={this.props.webchat.assignedTypeSelected}
                    setShowTeams={this.setShowTeams}
                    active={this.props.webchat.activeDashboard}
                    isCollapsed={this.state.isCollapsed}
                  />
                </ResizablePanel>
                <ResizableHandle
                  withHandle
                  className="max-md:hidden"
                  onDragging={this.handleDraggingHandle}
                />
              </>
            )}

            <ResizablePanel
              defaultSize={this.state.showNewLayout ? currentLayout[0] : currentLayout[1]}
              minSize={this.getListMinSize()}
              // z-0 keeps the list's sticky header under the thread on phones.
              className="max-md:absolute max-md:inset-0 max-md:z-0"
              inert={showMobileThread || undefined}
              aria-hidden={showMobileThread || undefined}
            >
              {this.props.webchat.activeDashboard === 'webchat' && (
                <CustomerList
                  searchForMessage={this.searchForMessage}
                  clearMessageSearch={this.clearMessageSearch}
                  customerDialogIsOpen={this.state.customerDialogIsOpen}
                  handleCustomerDialogIsOpen={this.handleCustomerDialogIsOpen}
                  activeConversation={this.props.webchat.activeConversation}
                  customerConversations={getFilteredCustomerConversationsFromSelectionState({
                    ...this.props.webchat,
                    _user: this.props.auth.user._id,
                  })}
                  openConversationsSelected={this.props.webchat.openConversationsSelected}
                  onSelectConversation={this.onSelectConversation}
                  disableConversationSelect={this.state.paymentSendInProgress}
                  loading={this.props.webchat.loading}
                  loadingMoreConversations={this.props.webchat.loadingMoreConversations}
                  getNextCustomers={this.getNextCustomers}
                  addConversationToCustomerConversations={
                    this.props.addConversationToCustomerConversations
                  }
                  setShowOnlyUnreadConversations={this.setShowOnlyUnreadConversations}
                  showOnlyUnreadConversations={this.props.webchat.showOnlyUnreadConversations}
                  assignedTypeSelected={this.props.webchat.assignedTypeSelected}
                  saveCustomer={this.saveCustomer}
                  active={this.props.webchat.activeDashboard}
                  isMobile={isMobile}
                  isCoveredOnMobile={showMobileThread}
                  allCustomerConversations={this.props.webchat.customerConversations}
                />
              )}
              {this.props.webchat.activeDashboard === 'group-chat' && (
                <>
                  <TeamConversations
                    handleShowStartNewChatDialog={this.handleShowStartNewChatDialog}
                    assignedTypeSelected={this.props.assignedTypeSelected}
                    setCustomerConversationAssignedType={this.setCustomerConversationAssignedType}
                    isMobile={isMobile}
                    onMobileBack={this.props.onMobileBack}
                  />
                  <NewChat
                    setCustomerConversationAssignedType={this.setCustomerConversationAssignedType}
                    handleShowStartNewChatDialog={this.handleShowStartNewChatDialog}
                    showStartNewChatDialog={this.state.showStartNewChatDialog}
                  />
                </>
              )}
            </ResizablePanel>
            <ResizableHandle
              withHandle
              className="max-md:hidden"
              onDragging={this.handleDraggingHandle}
            />
            <ResizablePanel
              defaultSize={this.state.showNewLayout ? currentLayout[1] : currentLayout[2]}
              minSize={30}
              className={cn(
                'max-md:absolute max-md:inset-0 max-md:z-10 max-md:bg-background',
                !showMobileThread && 'max-md:hidden',
                showMobileThread &&
                  this.props.animateMobilePush &&
                  'max-md:duration-300 max-md:ease-out max-md:animate-in max-md:slide-in-from-right motion-reduce:animate-none',
              )}
            >
              {this.renderMobileThreadPlaceholder()}
              {this.props.webchat.activeDashboard === 'webchat' && this.isComposeMode() && (
                <ComposeView
                  sendMessageToNewCustomer={this.sendMessageToNewCustomer}
                  isMobile={isMobile}
                  onMobileBack={this.props.onMobileBack}
                />
              )}
              {this.props.webchat.activeDashboard === 'webchat' && !this.isComposeMode() && (
                <div className="flex h-full w-full overflow-hidden relative">
                  <Messages
                    navigate={this.props.navigate}
                    goToCampaigns={this.props.goToCampaigns}
                    toggleMarkAsReadConversation={this.toggleMarkAsReadConversation}
                    loadingNewMessage={this.state.loadingNewMessage}
                    activeConversation={this.props.webchat.activeConversation}
                    sendMessage={this.sendMessage}
                    sendNote={this.sendNote}
                    sendMessageToNewCustomer={this.sendMessageToNewCustomer}
                    setPaymentSendInProgress={this.setPaymentSendInProgress}
                    setLoadingNewMessage={this.setLoadingNewMessage}
                    refreshScheduledMessages={this.refreshScheduledMessages}
                    registerScheduledMessageResolved={this.registerScheduledMessageResolved}
                    scheduledMessagesRefreshKey={this.state.scheduledMessagesRefreshKey}
                    messages={this.props.webchat.activeConversation.messages || []}
                    saveCustomer={this.saveCustomer}
                    addConversationToCustomerConversations={
                      this.props.addConversationToCustomerConversations
                    }
                    chatFeedRef={this.chatFeedRef}
                    customerDialogIsOpen={this.state.customerDialogIsOpen}
                    handleCustomerDialogIsOpen={this.handleCustomerDialogIsOpen}
                    addFolderToCustomerConversationLocally={
                      this.props.locallyAddFolderToCustomerConversation
                    }
                    removeFolderFromCustomerConversationLocally={
                      this.props.removeFolderFromCustomerConversation
                    }
                    toggleShowConversationDetails={this.toggleShowConversationDetails}
                    showConversationDetails={detailsShown}
                    isMobile={isMobile}
                    onMobileBack={this.props.onMobileBack}
                    onOpenDetails={isMobile ? this.props.openMobileDetails : undefined}
                  />

                  {!isMobile && this.renderConversationDetails()}
                </div>
              )}
              {this.props.webchat.activeDashboard === 'group-chat' && (
                <div
                  className={cn(
                    'flex h-full w-full overflow-hidden',
                    // 768-1279px: the info sidebar opens over the thread instead of squeezing it.
                    'md:max-xl:relative md:max-xl:[&>*:nth-child(2)]:absolute md:max-xl:[&>*:nth-child(2)]:inset-y-0 md:max-xl:[&>*:nth-child(2)]:right-0 md:max-xl:[&>*:nth-child(2)]:z-20 md:max-xl:[&>*:nth-child(2)]:w-80 md:max-xl:[&>*:nth-child(2)]:max-w-[85%] md:max-xl:[&>*:nth-child(2)]:shadow-xl',
                  )}
                >
                  <div
                    className={cn(
                      'flex flex-col h-full transition duration-300 ease-in-out overflow-hidden md:max-xl:relative',
                      this.state.isSidebarOpen && !isMobile ? 'w-2/3 max-xl:w-full' : 'w-full',
                    )}
                  >
                    {this.state.isSidebarOpen && !isMobile && activeConversationExists && (
                      // 768-1279px: the info sidebar covers the thread's header button,
                      // so a tap anywhere on the thread closes it (pointer only; the
                      // sidebar has its own close button and Escape).
                      <button
                        type="button"
                        aria-hidden="true"
                        tabIndex={-1}
                        onClick={this.toggleSidebar}
                        className="absolute inset-0 z-10 hidden cursor-default bg-black/10 md:max-xl:block"
                      />
                    )}
                    {!activeConversationExists ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flexGrow: 1,
                        }}
                        className="bg-background bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:24px_24px]"
                      >
                        <div
                          style={{
                            margin: 6,
                            fontSize: 16,
                            color: '#8d8d8d',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '80%',
                          }}
                        >
                          <div className="w-full max-w-xl p-4">
                          <SignupSampleDashboard feature="team" realCount={this.props.teams.teamConversations?.length || 0} ready={this.props.teams.initialLoad?.status === 'ready' && this.props.teams.initialLoad.businessId === this.props.auth?._id}>
                          <div className="rounded-2xl border border-border/70 bg-card/80 p-6 text-center shadow-[0_1px_2px_rgba(0,0,0,.04),0_8px_24px_-12px_rgba(0,0,0,.12)] dark:border-white/[0.08] dark:bg-card/60">
                            <div className="mx-auto mb-3 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                              <MessagesSquare className="size-6" />
                            </div>
                            <h3 className="text-[17px] font-medium text-foreground">Talk with your team</h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Start a group chat or a direct message to get going.
                            </p>
                            <Button onClick={this.handleShowStartNewChatDialog} className="mt-4 rounded-xl">
                              Start a chat
                            </Button>
                          </div>
                          </SignupSampleDashboard>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <MessageNavbar
                          onSidebarToggle={isMobile ? this.props.openTeamInfo : this.toggleSidebar}
                          infoToggleRef={this.teamInfoToggleRef}
                          handleIsAttachmentsModalOpen={this.handleIsAttachmentsModalOpen}
                          isAttachmentsModalOpen={this.state.isAttachmentsModalOpen}
                          isMobile={isMobile}
                          onMobileBack={this.props.onMobileBack}
                        />

                        <TeamMessages
                          key={this.props.teams._activeTeamConversation}
                          infiniteScrollHeight={
                            this.props.isTablet || isMobile ? 'min-h-0 flex-1' : undefined
                          }
                          addConversationToCustomerConversations={
                            this.props.addConversationToCustomerConversations
                          }
                          setOpenOrClosedConversationsSelected={
                            this.props.setOpenOrClosedConversationsSelected
                          }
                          setCustomerConversationAssignedType={
                            this.setCustomerConversationAssignedType
                          }
                          setShowTeams={this.setShowTeams}
                          assignedTypeSelected={this.props.webchat.assignedTypeSelected}
                        />
                        <div className="flex flex-col">
                          <Composer
                            key={this.props.teams._activeTeamConversation}
                            isSidebarOpen={isMobile ? false : this.state.isSidebarOpen}
                            isMobile={isMobile}
                            onMobileBack={this.props.onMobileBack}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <Sidebar
                    open={isMobile ? Boolean(this.props.teamInfoOpen) : this.state.isSidebarOpen}
                    onClose={
                      isMobile
                        ? this.props.closeTeamInfo
                        : this.props.isTablet
                          ? this.closeTeamInfoOverlay
                          : undefined
                    }
                    closeButtonRef={this.teamInfoCloseRef}
                    assignedTypeSelected={this.props.assignedTypeSelected}
                    handleIsAttachmentsModalOpen={this.handleIsAttachmentsModalOpen}
                    isAttachmentsModalOpen={this.state.isAttachmentsModalOpen}
                    isMobile={isMobile}
                    onMobileBack={this.props.onMobileBack}
                  />
                </div>
              )}
            </ResizablePanel>
          </ResizablePanelGroup>
        </TooltipProvider>
        {this.renderMobilePages()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  webchat: state.webchat,
  auth: state.auth,
  user: state.auth.user,
  users: state.users,
  a2pCompliance: state.a2pCompliance,
  teams: state.teams,
  userInterface: state.userInterface,
});

const ConnectedWebchatDashboard = connect(mapStateToProps, {
  handleDisableNotificationsOnLogout,
  setOpenOrClosedConversationsSelected,
  setCustomerConversationAssignedType,
  setShowOnlyUnreadConversations,
  getCustomerConversations,
  stripeAccountConnected,
  getInitialTeamConversations,
  setConversationToRead,
  setActiveConversation,
  toggleMarkAsReadConversation,
  locallyAddFolderToCustomerConversation,
  removeFolderFromCustomerConversation,
  addConversationToCustomerConversations,
  setCustomerConversations,
  deleteFolderFromAllCustomerConversations,
  displayNotification,
  setActiveDashboard,
  setShowConversationDetails,
  setTranscriptView,
  failOptimisticMessage,
  removeOptimisticMessage,
  clearEscalationIfClosed,
})(WebchatDashboard);

// Avoid a duplicate catch-up immediately after the normal conversation fetch.
const WEBCHAT_DASHBOARD_CATCH_UP_THROTTLE_MS = 2 * 60 * 1000;

const WebchatDashboardWrapper = ({ assignedType }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const lastDashboardCatchUpAt = useRef(0);

  const { _folder, _location, _user, _tag, _department, _customerConversation, _teamConversation } =
    useParams();
  const dispatch = useDispatch();

  const { assignedTypeSelected, customerConversations, loading } = useSelector(
    (state) => state.webchat,
  );
  const lastCustomerConversationsSyncedAt = useSelector(
    (state) => state.webchat.lastCustomerConversationsSyncedAt,
  );
  const { user } = useSelector((state) => state.auth);
  const businessGroup = useSelector((state) => state.businessGroup);

  const businessId = useSelector((state) => state.auth?._id);

  const teams = useSelector((state) => state.teams);
  const _activeTeamConversation = teams._activeTeamConversation;

  const activeTeamConversation = teams.teamConversations.find(
    (tc) => tc._id === _activeTeamConversation,
  );

  useEffect(() => {
    if (!location.pathname) {
      return;
    }

    if (loading) {
      return;
    }

    const now = Date.now();
    if (
      lastCustomerConversationsSyncedAt &&
      now - lastCustomerConversationsSyncedAt < WEBCHAT_DASHBOARD_CATCH_UP_THROTTLE_MS
    ) {
      return;
    }

    if (now - lastDashboardCatchUpAt.current < WEBCHAT_DASHBOARD_CATCH_UP_THROTTLE_MS) {
      return;
    }

    lastDashboardCatchUpAt.current = now;
    dispatch(syncCustomerConversationsAfterIdle());
  }, [dispatch, lastCustomerConversationsSyncedAt, loading, location.pathname]);

  const setDashboardType = (type, dashboardType) => {
    dispatch(setCustomerConversationAssignedType(type));
    dispatch(setActiveDashboard(dashboardType));
    dispatch(setActiveTeamConversationToNull());
  };

  const handleWebchat = (type, param) => {
    setDashboardType(`${type}_${param}`, 'webchat');
  };

  const handleSwitchLocation = (selectedBusiness, goToRootUrl = true) => {
    dispatch(switchUser({ _business: selectedBusiness._id, goToRootUrl }));
  };

  const checkForTeamsConversationLinkInParams = async () => {
    if (searchParams.get('_teamMention')) return;
    const { pathname } = location;

    // make sure pathname includes /conversations/groups
    if (!pathname.includes('/conversation/groups')) {
      return;
    }

    if (!_teamConversation) {
      return null;
    }

    const _business = searchParams.get('_location');
    const _teamMessage = searchParams.get('_teamMessage');
    const isDirectMessageString = searchParams.get('isDirectMessage');

    const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;

    if (
      !checkForHexRegExp.test(_teamConversation) ||
      (!checkForHexRegExp.test(_business) && !checkForHexRegExp.test(_teamMessage))
    ) {
      return null;
    }

    const isDirectMessage = JSON.parse(isDirectMessageString);
    // check if the teamConversation is part of the current dashboard, or another location
    const activeBusiness = user._temporaryBusiness;

    // if activeBusiness !== _business, check if the _business is in businessGroup, if it is switch to that location
    if (
      activeBusiness &&
      activeBusiness !== _business &&
      businessGroup.map((bg) => bg._id).includes(_business)
    ) {
      // switch to location
      return handleSwitchLocation({ _id: _business }, false);
    }

    dispatch(
      getSurroundingMessages({
        _teamConversation,
        _teamMessage,
      }),
    );

    if (_teamConversation && _teamMessage && _teamConversation !== _activeTeamConversation) {
      navigate(
        `/groupchat/${
          isDirectMessage ? 'dms' : 'groups'
        }/conversation/groups/${_teamConversation}?_location=${_business}&_teamMessage=${_teamMessage}&isDirectMessage=${isDirectMessage}`,
      );
      dispatch(setActiveTeamConversation(_teamConversation));
    }

    dispatch(setCustomerConversationAssignedType(isDirectMessage ? 'DIRECT_MESSAGES' : 'GROUPS'));
  };

  const checkForConversationLinkInParams = async () => {
    const { pathname } = location;

    if (isTeamConversationPath(pathname)) {
      return;
    }

    // Check if this is a direct /conversation URL that needs to be redirected to /inbox/conversation
    if (pathname.startsWith('/conversation/') && !pathname.includes('/inbox')) {
      const conversationBusinessId = searchParams.get('_location');
      const redirectPath = buildLegacyCustomerConversationRedirectPath({
        pathname,
        _business: conversationBusinessId,
      });

      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      }

      return;
    }

    if (!pathname.includes('/conversation')) {
      return;
    }

    if (!_customerConversation) {
      return null;
    }

    // `/locations/:_location` is the list filter. `?_location=` is the conversation's business.
    const conversationBusinessId = _location || searchParams.get('_location');

    const checkForHexRegExp = /^[0-9a-fA-F]{24}$/;

    if (!checkForHexRegExp.test(_customerConversation)) {
      return null;
    }

    const existingCustomerConversation = customerConversations.find(
      (c) => c._id === _customerConversation,
    );

    const hasValidBusiness = checkForHexRegExp.test(conversationBusinessId);

    // Direct links may need a location switch, but in-app conversation clicks
    // already have the conversation loaded and should remain SPA navigation.
    if (hasValidBusiness) {
      const activeBusiness = user._temporaryBusiness;

      if (
        !existingCustomerConversation &&
        !user.isEnterpriseEnabled &&
        activeBusiness &&
        activeBusiness !== conversationBusinessId &&
        businessGroup.map((bg) => bg._id).includes(conversationBusinessId)
      ) {
        return handleSwitchLocation({ _id: conversationBusinessId }, false);
      }
    }

    let customerConversation = {};

    // if the conversation doesn't exist in the customerConversations store, get it from the server, add it to the store and set it as active
    // otherwise if it exists, set it as active
    if (!existingCustomerConversation) {
      const response = await axios.post(
        `${getWebchatUrl(window.location.href)}/api/webchat/get-messages-from-customer`,
        {
          _customerConversation,
        },
      );

      const { data } = response;

      if (data.error) {
        // individuals (contributors) can only open conversations assigned to
        // them — send them back to the inbox instead of a blank thread
        if (data.accessDenied) {
          notifyConversationAccessDenied();
          navigate('/inbox', { replace: true });
        }
        return;
      }

      customerConversation = data.customerConversation;

      // add the conversation to the customerConversations
      await dispatch(addConversationToCustomerConversations(customerConversation, true));
    } else {
      customerConversation = existingCustomerConversation;
      dispatch(setActiveConversation(customerConversation._id));

      if (existingCustomerConversation.messagesPreviewOnly) {
        const fullRes = await axios.post(
          `${getWebchatUrl(window.location.href)}/api/webchat/get-messages-from-customer`,
          { _customerConversation },
        );

        if (!fullRes.data.error && fullRes.data.customerConversation) {
          dispatch(addConversationToCustomerConversations(fullRes.data.customerConversation));
        }
      }
    }

    // set the open or closed state on the dashboard based on conversation.open
    if (customerConversation) {
      dispatch(setOpenOrClosedConversationsSelected(customerConversation.open));
    }
  };

  // if the current team conversation type (dm or groupchat) does not match the dashboard type we are trying to set the dashboard to
  // set the active team conversation to null
  const checkAndSetTeamConversationAndDashboardBasedOnLastConversationType = (
    dashboardType,
    isDirectMessage,
  ) => {
    // if the dashboard type is groups or direct messages, and the active team conversation is a customer notepad from runner, set the active team conversation to null
    if (dashboardType === 'GROUPS' || dashboardType === 'DIRECT_MESSAGES') {
      if (activeTeamConversation?.isRunner) {
        dispatch(setActiveTeamConversationToNull());
        dispatch(setCustomerConversationAssignedType(dashboardType));
        dispatch(setActiveDashboard('group-chat'));
        return;
      }
    }

    if (
      assignedTypeSelected !== dashboardType &&
      activeTeamConversation?.isDirectMessage !== isDirectMessage
    ) {
      dispatch(setActiveTeamConversationToNull());
    }
    dispatch(setCustomerConversationAssignedType(dashboardType));
    dispatch(setActiveDashboard('group-chat'));
  };

  // Whether an inbox list (not team chat) was the last list shown here.
  const inboxListShownRef = useRef(false);
  // Whether compose is showing a phone's kept list underneath (see below).
  const composeKeptListRef = useRef(false);

  // this useEffect is used to set the active dashboard based on the assignedType passed by the Router, will run every-time the WebchatDashboard is mounted and the url changes
  // Allows us to render different dashboards based on the assignedType
  // assigned types: ALL | UNASSIGNED | MY_MESSAGES | USER_ | DEPARTMENT_ | LOCATION_ | GROUPS | DIRECT_MESSAGES | FOLDER_
  // biome-ignore lint/correctness/useExhaustiveDependencies: route handlers dispatch Redux and are recreated each render, so depend on route primitives to avoid update loops.
  useEffect(() => {
    composeKeptListRef.current = false;
    if (location.pathname.includes('/groupchat/dms')) {
      inboxListShownRef.current = false;
      checkAndSetTeamConversationAndDashboardBasedOnLastConversationType('DIRECT_MESSAGES', true);
      return;
    }

    if (location.pathname.includes('/groupchat/groups')) {
      inboxListShownRef.current = false;
      checkAndSetTeamConversationAndDashboardBasedOnLastConversationType('GROUPS', false);
      return;
    }

    // Phones: compose (/inbox/new) covers the list it was opened from. Keep
    // that list (Unassigned, a folder…) underneath instead of switching it to
    // the Inbox, so Back finds it as it was, scroll position included. Wider
    // screens show the list beside compose and switch to the Inbox as before.
    if (location.pathname.endsWith('/new') && inboxListShownRef.current && isMobileViewport()) {
      composeKeptListRef.current = true;
      return;
    }

    if (assignedType) {
      inboxListShownRef.current = true;
      if (assignedType.includes('FOLDER_')) {
        handleWebchat('FOLDER', _folder);
        return;
      }
      if (assignedType.includes('LOCATION_')) {
        handleWebchat('LOCATION', _location);
        return;
      }

      if (assignedType.includes('USER_')) {
        handleWebchat('USER', _user);
        return;
      }

      if (assignedType.includes('TAG_')) {
        handleWebchat('TAG', _tag);
        return;
      }

      if (assignedType.includes('DEPARTMENT_')) {
        handleWebchat('DEPARTMENT', _department);
        return;
      }

      setDashboardType(assignedType, 'webchat');
      dispatch(setActiveTeamConversationToNull());
      return;
    }
  }, [
    assignedType,
    location.pathname,
    _department,
    _folder,
    _location,
    _tag,
    _user,
    dispatch,
  ]);

  // looks for _conversation and _location in the URL and redirects the user to the correct location and sets the right conversation as active
  // biome-ignore lint/correctness/useExhaustiveDependencies: link handlers dispatch Redux and are recreated each render, so depend on route primitives to avoid update loops.
  useEffect(() => {
    if (!loading) {
      checkForConversationLinkInParams();
      checkForTeamsConversationLinkInParams();
    }
  }, [
    loading,
    location.pathname,
    location.search,
    _customerConversation,
    _teamConversation,
  ]);

  // Clear the active conversation when navigating to a route without a conversation param
  useEffect(() => {
    if (!_customerConversation && !_teamConversation && !loading) {
      dispatch(setActiveConversation(''));
    }
  }, [_customerConversation, _teamConversation, loading, dispatch]);

  const [isTablet, setIsTablet] = useState(window.innerWidth < 1280);

  useEffect(() => {
    const handleResize = () => setIsTablet(window.innerWidth < 1280);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ----- Phones: the list and the thread are separate screens -----
  const isMobile = useIsMobile();
  const navigationType = useNavigationType();
  // Slide a thread in only when it was opened on a phone, not when rotating
  // a wider screen (where it was already showing) down to phone width.
  const navigationRef = useRef({ key: location.key, onPhone: isMobile });
  if (navigationRef.current.key !== location.key) {
    navigationRef.current = { key: location.key, onPhone: isMobile };
  }
  // Widening past a phone mid-compose puts the list beside compose, so show
  // the Inbox there as opening compose at that width does (and as the route
  // and the sidebar say) instead of the phone's kept list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs only when the size class changes.
  useEffect(() => {
    if (isMobile || !composeKeptListRef.current) return;
    composeKeptListRef.current = false;
    if (assignedTypeSelected !== assignedType) setDashboardType(assignedType, 'webchat');
  }, [isMobile]);

  const detailsPanel = useMobilePanel('details');
  const transcriptPanel = useMobilePanel('transcript');
  const teamInfoPanel = useMobilePanel('teamInfo');
  const transcriptView = useSelector((state) => state.userInterface.webchat.transcriptView);

  // The history entry of the list screen, so Back from a thread can pop to it
  // (keeping its scroll, search and filters) instead of pushing a new one.
  const listEntryRef = useRef(null);
  const isListRoute =
    !_customerConversation && !_teamConversation && !location.pathname.endsWith('/new');

  useEffect(() => {
    if (isListRoute) {
      listEntryRef.current = { idx: window.history.state?.idx ?? 0 };
    }
  }, [isListRoute, location.key]);

  const { pathname, search } = location;
  const onMobileBack = useCallback(() => {
    const idx = window.history.state?.idx;
    const listEntry = listEntryRef.current;
    if (listEntry && typeof idx === 'number' && idx === listEntry.idx + 1) {
      navigate(-1);
      return;
    }
    navigate(getConversationListPath(pathname, search), { replace: true });
  }, [navigate, pathname, search]);

  // Phones: a team chat address whose chat isn't in the loaded list (left or
  // deleted, then reached with Forward, or a stale link) goes back to the list
  // instead of an empty thread.
  const teamThreadListed =
    Boolean(_teamConversation) && teams.teamConversations.some((tc) => tc._id === _teamConversation);
  const teamListReady =
    teams.initialLoad?.status === 'ready' && teams.initialLoad.businessId === businessId;
  const linkBusinessId = searchParams.get('_location');
  // A link to another location's chat switches location first.
  const switchingForTeamLink =
    Boolean(linkBusinessId) &&
    linkBusinessId !== businessId &&
    businessGroup.some((bg) => bg._id === linkBusinessId);
  useEffect(() => {
    if (!isMobile || !_teamConversation || teamThreadListed || !teamListReady) return;
    if (switchingForTeamLink) return;
    // Leave and Delete take their thread off the stack themselves, often with
    // a Back first; give that a moment so the two don't both move.
    const timer = setTimeout(
      () => navigate(getConversationListPath(pathname, search), { replace: true }),
      300,
    );
    return () => clearTimeout(timer);
  }, [
    isMobile,
    _teamConversation,
    teamThreadListed,
    teamListReady,
    switchingForTeamLink,
    navigate,
    pathname,
    search,
  ]);

  // Phones decide list or thread from the URL. Make the opened team chat the
  // active one, e.g. after Back then a browser Forward.
  useEffect(() => {
    if (!isMobile || !_teamConversation || _teamConversation === _activeTeamConversation) {
      return;
    }
    if (!teams.teamConversations.some((tc) => tc._id === _teamConversation)) {
      return;
    }
    dispatch(setActiveTeamConversation(_teamConversation));
  }, [isMobile, _teamConversation, _activeTeamConversation, teams.teamConversations, dispatch]);

  // A call transcript opened from the thread is its own phone screen.
  // Closing it (Back, swipe) clears the transcript, and a stale screen left in
  // history without one closes itself.
  const transcriptShownRef = useRef(false);
  const { isOpen: transcriptOpen, open: openTranscript, close: closeTranscript } = transcriptPanel;
  useEffect(() => {
    if (!isMobile) return;
    if (transcriptOpen) {
      transcriptShownRef.current = true;
      if (!transcriptView) closeTranscript();
      return;
    }
    if (transcriptShownRef.current) {
      transcriptShownRef.current = false;
      if (transcriptView) dispatch(setTranscriptView(null));
      return;
    }
    if (transcriptView) openTranscript();
  }, [isMobile, transcriptOpen, transcriptView, openTranscript, closeTranscript, dispatch]);

  return (
    <ConnectedWebchatDashboard
      navigate={navigate}
      location={location}
      isTablet={isTablet}
      isMobile={isMobile}
      routeCustomerConversationId={_customerConversation}
      routeTeamConversationId={_teamConversation}
      onMobileBack={onMobileBack}
      animateMobilePush={navigationType === 'PUSH' && navigationRef.current.onPhone}
      mobileDetailsOpen={detailsPanel.isOpen}
      openMobileDetails={detailsPanel.open}
      closeMobileDetails={detailsPanel.close}
      transcriptOpen={transcriptOpen}
      closeTranscript={closeTranscript}
      teamInfoOpen={teamInfoPanel.isOpen}
      openTeamInfo={teamInfoPanel.open}
      closeTeamInfo={teamInfoPanel.close}
    />
  );
};

export default WebchatDashboardWrapper;
